import { createTheme, alpha, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

// ALTERAÇÃO: A paleta 'brand' agora contém suas cores primárias (verdes),
// mantendo o nome da constante para compatibilidade.
export const brand = {
  50: 'hsl(116, 100%, 95%)',
  100: 'hsl(116, 100%, 88%)',
  200: 'hsl(116, 99%, 77%)',
  300: '#78ff6c' /* hsl(116, 100%, 71%) */, // Sua cor de detalhe mais clara
  400: 'hsl(121, 65%, 55%)',
  500: '#4bdf4d' /* hsl(121, 73%, 58%) */, // Sua cor de detalhe principal
  600: 'hsl(121, 73%, 45%)',
  700: 'hsl(121, 73%, 35%)',
  800: 'hsl(121, 73%, 25%)',
  900: 'hsl(121, 73%, 15%)',
};

// ALTERAÇÃO: A paleta 'gray' agora contém suas cores neutras de fundo/texto,
// mantendo o nome da constante para compatibilidade.
export const gray = {
  50: '#fffcf2' /* hsl(48, 83%, 98%) */, // Sua cor de fundo clara
  100: 'hsl(48, 30%, 94%)',
  200: 'hsl(48, 15%, 88%)',
  300: 'hsl(45, 10%, 75%)',
  400: 'hsl(40, 5%, 60%)',
  500: 'hsl(30, 5%, 50%)',
  600: 'hsl(0, 0%, 40%)',
  700: 'hsl(0, 0%, 25%)',
  800: 'hsl(0, 0%, 15%)',
  900: '#0e0e0e' /* hsl(0, 0%, 5%) */, // Sua cor de fundo escura
};

// As paletas de cores de status (success, warning, error) foram mantidas.
export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

// AVISO: Esta função foi atualizada para usar as novas paletas,
// mas o código principal do seu template provavelmente usa `colorSchemes`.
export const getDesignTokens = (mode: PaletteMode) => {
  const isDarkMode = mode === 'dark';

  customShadows[1] = isDarkMode
    ? 'hsla(0, 0%, 0%, 0.7) 0px 4px 16px 0px, hsla(0, 0%, 5%, 0.8) 0px 8px 16px -5px'
    : 'hsla(0, 0%, 0%, 0.07) 0px 4px 16px 0px, hsla(0, 0%, 5%, 0.07) 0px 8px 16px -5px';

  return {
    palette: {
      mode,
      primary: {
        light: brand[300],
        main: brand[500],
        dark: brand[700],
        contrastText: gray[900],
        ...(isDarkMode && {
          contrastText: gray[900],
        }),
      },
      info: { // A cor 'info' ainda usa 'brand' por padrão nos templates.
        light: brand[100], main: brand[300], dark: brand[600], contrastText: gray[50],
        ...(isDarkMode && { contrastText: brand[300], light: brand[500], main: brand[700], dark: brand[900] }),
      },
      warning: {
        light: orange[300], main: orange[400], dark: orange[800],
        ...(isDarkMode && { light: orange[400], main: orange[500], dark: orange[700] }),
      },
      error: {
        light: red[300], main: red[400], dark: red[800],
        ...(isDarkMode && { light: red[400], main: red[500], dark: red[700] }),
      },
      success: {
        light: green[300], main: green[400], dark: green[800],
        ...(isDarkMode && { light: green[400], main: green[500], dark: green[700] }),
      },
      grey: { ...gray },
      divider: isDarkMode ? alpha(gray[700], 0.4) : alpha(gray[300], 0.5),
      background: {
        default: isDarkMode ? gray[900] : gray[50],
        paper: isDarkMode ? 'hsl(0, 0%, 8%)' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? gray[50] : gray[900],
        secondary: isDarkMode ? gray[400] : gray[700],
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[isDarkMode ? 700 : 200], 0.2),
        selected: alpha(gray[isDarkMode ? 700 : 200], 0.3),
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: { fontSize: defaultTheme.typography.pxToRem(48), fontWeight: 600, lineHeight: 1.2, letterSpacing: -0.5, },
      h2: { fontSize: defaultTheme.typography.pxToRem(36), fontWeight: 600, lineHeight: 1.2, },
      h3: { fontSize: defaultTheme.typography.pxToRem(30), lineHeight: 1.2, },
      h4: { fontSize: defaultTheme.typography.pxToRem(24), fontWeight: 600, lineHeight: 1.5, },
      h5: { fontSize: defaultTheme.typography.pxToRem(20), fontWeight: 600, },
      h6: { fontSize: defaultTheme.typography.pxToRem(18), fontWeight: 600, },
      subtitle1: { fontSize: defaultTheme.typography.pxToRem(18), },
      subtitle2: { fontSize: defaultTheme.typography.pxToRem(14), fontWeight: 500, },
      body1: { fontSize: defaultTheme.typography.pxToRem(14), },
      body2: { fontSize: defaultTheme.typography.pxToRem(14), fontWeight: 400, },
      caption: { fontSize: defaultTheme.typography.pxToRem(12), fontWeight: 400, },
    },
    shape: { borderRadius: 8, },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[300],
        main: brand[500],
        dark: brand[700],
        contrastText: gray[900], // Texto escuro (#0e0e0e) para botões primários
      },
      info: { // A cor 'info' ainda usa 'brand' por padrão nos templates.
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: { light: orange[300], main: orange[400], dark: orange[800], },
      error: { light: red[300], main: red[400], dark: red[800], },
      success: { light: green[300], main: green[400], dark: green[800], },
      grey: { ...gray, },
      divider: alpha(gray[300], 0.5),
      background: {
        default: gray[50], // Sua cor de fundo clara: #fffcf2
        paper: '#ffffff', // Fundo de "cartões" um pouco diferente para se destacar
      },
      text: {
        primary: gray[900], // Sua cor de texto principal: #0e0e0e
        secondary: gray[700], // Cor de texto secundário, um pouco mais clara
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[200], 0.2),
        selected: `${alpha(gray[200], 0.3)}`,
      },
      baseShadow: 'hsla(0, 0%, 0%, 0.07) 0px 4px 16px 0px, hsla(0, 0%, 5%, 0.07) 0px 8px 16px -5px',
    },
  },
  dark: {
    palette: {
      primary: {
        light: brand[300],
        main: brand[400], // Um tom um pouco mais claro no modo escuro para contraste
        dark: brand[600],
        contrastText: gray[900], // Texto escuro (#0e0e0e) para botões primários
      },
      info: { // A cor 'info' ainda usa 'brand' por padrão nos templates.
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: { light: orange[400], main: orange[500], dark: orange[700], },
      error: { light: red[400], main: red[500], dark: red[700], },
      success: { light: green[400], main: green[500], dark: green[700], },
      grey: { ...gray, },
      divider: alpha(gray[700], 0.4),
      background: {
        default: gray[900], // Sua cor de fundo escura: #0e0e0e
        paper: 'hsl(0, 0%, 8%)', // Fundo de "cartões" um pouco mais claro
      },
      text: {
        primary: gray[50], // Sua cor de texto clara: #fffcf2
        secondary: gray[400], // Cor de texto secundário, um pouco mais escura
      },
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3),
      },
      baseShadow: 'hsla(0, 0%, 0%, 0.7) 0px 4px 16px 0px, hsla(0, 0%, 5%, 0.8) 0px 8px 16px -5px',
    },
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

// @ts-ignore
const defaultShadows: Shadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;